import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../services/api';

interface MasterPasswordData {
  configured: boolean;
  password: string | null;
  updatedAt: string | null;
  updatedBy: string | null;
  failOpenSeconds: number;
}

/**
 * Break-glass master password (super_user only).
 *
 * When the backend is DOWN, every normal unlock path fails (they all need the API).
 * This password is validated locally by the Windows Agent against a cached hash, so a
 * supervisor can open a locked screen with no network. It works ONLY while the agent
 * considers the backend unreachable. Layer 1 (fail-open) auto-releases the gate after
 * the grace window even if nobody types anything.
 */
const BreakGlassMasterPasswordCard: React.FC = () => {
  const queryClient = useQueryClient();
  const [reveal, setReveal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [failOpen, setFailOpen] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['enforcementMasterPassword'],
    queryFn: () => apiClient.enforcement.getMasterPassword(),
  });

  const config: MasterPasswordData | undefined = data?.data?.data;

  useEffect(() => {
    if (config?.failOpenSeconds != null) {
      setFailOpen(String(config.failOpenSeconds));
    }
  }, [config?.failOpenSeconds]);

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['enforcementMasterPassword'] });

  const setMutation = useMutation({
    mutationFn: (password: string) => apiClient.enforcement.setMasterPassword(password),
    onSuccess: () => {
      setNewPassword('');
      setNotice('Master password saved. It will reach agents on their next sync.');
      setError(null);
      invalidate();
    },
    onError: (e: any) =>
      setError(e?.response?.data?.message || 'Failed to save master password.'),
  });

  const rotateMutation = useMutation({
    mutationFn: () => apiClient.enforcement.rotateMasterPassword(),
    onSuccess: () => {
      setReveal(true);
      setNotice('Master password rotated. The previous one no longer works once agents sync.');
      setError(null);
      invalidate();
    },
    onError: (e: any) =>
      setError(e?.response?.data?.message || 'Failed to rotate master password.'),
  });

  const failOpenMutation = useMutation({
    mutationFn: (seconds: number) => apiClient.enforcement.setFailOpenSeconds(seconds),
    onSuccess: () => {
      setNotice('Fail-open window saved.');
      setError(null);
      invalidate();
    },
    onError: (e: any) =>
      setError(e?.response?.data?.message || 'Failed to save fail-open window.'),
  });

  const copyPassword = () => {
    if (config?.password) {
      navigator.clipboard?.writeText(config.password).catch(() => undefined);
      setNotice('Copied to clipboard.');
    }
  };

  const busy = setMutation.isPending || rotateMutation.isPending || failOpenMutation.isPending;

  return (
    <Card sx={{ mt: 3, border: '1px solid', borderColor: 'warning.light' }}>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <Typography variant="h6">🔓 Break-glass Master Password</Typography>
          <Chip size="small" color="warning" label="Super User only" />
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Used to unlock an agent screen when TEKS TIME is unreachable (the normal unlock needs the
          API). The agent validates it offline against a cached copy, so it only takes effect once a
          computer has synced at least once. After an outage, rotate it so the one you used is retired.
        </Typography>

        {notice && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>
            {notice}
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {isLoading ? (
          <CircularProgress size={24} />
        ) : (
          <>
            <Box sx={{ mb: 2 }}>
              {config?.configured ? (
                <TextField
                  label="Current master password"
                  value={config.password ?? '••••••••'}
                  type={reveal ? 'text' : 'password'}
                  fullWidth
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title={reveal ? 'Hide' : 'Reveal'}>
                          <IconButton onClick={() => setReveal((v) => !v)} edge="end">
                            {reveal ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Copy">
                          <IconButton onClick={copyPassword} edge="end">
                            <ContentCopyIcon />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                />
              ) : (
                <Alert severity="info">
                  No master password is set yet. Enter one below to enable offline break-glass unlock.
                </Alert>
              )}
              {config?.configured && (config.updatedAt || config.updatedBy) && (
                <Typography variant="caption" color="text.secondary">
                  Last changed{config.updatedAt ? ` ${new Date(config.updatedAt).toLocaleString()}` : ''}
                  {config.updatedBy ? ` by ${config.updatedBy}` : ''}.
                </Typography>
              )}
            </Box>

            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
              <Button
                variant="contained"
                color="warning"
                disabled={busy}
                onClick={() => rotateMutation.mutate()}
              >
                {config?.configured ? 'Rotate (generate new)' : 'Generate a password'}
              </Button>
            </Stack>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Set a specific password
            </Typography>
            <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
              <TextField
                label="New master password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                size="small"
                sx={{ flex: 1 }}
                helperText="At least 6 characters. Supervisors read this aloud, so keep it simple."
              />
              <Button
                variant="outlined"
                disabled={busy || newPassword.trim().length < 6}
                onClick={() => setMutation.mutate(newPassword.trim())}
                sx={{ height: 40 }}
              >
                Save
              </Button>
            </Stack>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Layer 1 — fail-open window
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              If an agent can&apos;t reach TEKS TIME for this many seconds while a screen is locked, the
              agent auto-releases the gate on its own — so nobody is ever trapped during an outage.
            </Typography>
            <Stack direction="row" spacing={2}>
              <TextField
                label="Seconds"
                type="number"
                value={failOpen}
                onChange={(e) => setFailOpen(e.target.value)}
                size="small"
                inputProps={{ min: 60, max: 3600 }}
                helperText="60–3600 (default 300 = 5 min)"
              />
              <Button
                variant="outlined"
                disabled={busy || !failOpen}
                onClick={() => failOpenMutation.mutate(Number(failOpen))}
                sx={{ height: 40 }}
              >
                Save window
              </Button>
            </Stack>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default BreakGlassMasterPasswordCard;
